import React, { useState, useEffect } from "react";
import { baseurl } from "../../baseurl";
import axios from "axios";
import { FiPhone, FiMail, FiMapPin, FiHash, FiCalendar, FiFileText, FiCircle } from "react-icons/fi";

/* ─────────────────────────────────────────────
   INLINE CSS — "Blueprint" design
   White paper + indigo top band + ruled table + stamp watermark
───────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600;700&family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');

.it6__wrap * { box-sizing: border-box; margin: 0; padding: 0; }

.it6__wrap {
  font-family: 'IBM Plex Sans', sans-serif;
  background: #dde8e8;
  min-height: 100vh; 
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

/* ── A4 paper ── */
.it6__paper {
  width: 210mm;
  min-height: 297mm;
  background: #fefefe;
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(10,85,86,0.18), 0 2px 8px rgba(10,85,86,0.10);
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* ── watermark stamp ── */
.it6__stamp {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-28deg);
  width: 200px; height: 200px;
  border-radius: 50%;
  border: 6px double rgba(10,85,86,0.07);
  display: flex; align-items: center; justify-content: center;
  pointer-events: none;
  z-index: 0;
}
.it6__stamp-inner {
  width: 170px; height: 170px; border-radius: 50%;
  border: 3px solid rgba(10,85,86,0.05);
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px;
}
.it6__stamp-word {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 13px; font-weight: 700; letter-spacing: 0.3em; text-transform: uppercase;
  color: rgba(10,85,86,0.08);
}
.it6__stamp-line {
  width: 60px; height: 2px; background: rgba(10,85,86,0.07);
}

/* ── content wrapper (above watermark) ── */
.it6__content { position: relative; z-index: 1; display: flex; flex-direction: column; flex: 1; }

/* ══════════════════════════════════════
   TOP BAND
══════════════════════════════════════ */
.it6__top-band {
  background: #0a3d3e;
  padding: 0;
  display: flex;
  align-items: stretch;
}

/* colored left tab */
.it6__tab {
  width: 6px;
  flex-shrink: 0;
  background: linear-gradient(180deg, #FFD166, #f59e0b);
}

.it6__band-inner {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 26px 36px;
  gap: 20px;
  flex-wrap: wrap;
}

.it6__company-name {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 20px; font-weight: 700; color: #ffffff;
  letter-spacing: -0.01em; margin-bottom: 8px;
}
.it6__seller-row {
  display: flex; align-items: center; gap: 7px;
  font-size: 11.5px; color: rgba(255,255,255,0.55); margin-bottom: 4px;
  font-family: 'IBM Plex Sans', sans-serif;
}
.it6__seller-row svg { color: #FFD166; flex-shrink: 0; }
.it6__gstin-mono {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px; font-weight: 600; color: rgba(255,209,102,0.70);
  letter-spacing: 0.06em; margin-top: 4px;
}

/* invoice number block (right of band) */
.it6__inv-block { text-align: right; }
.it6__inv-label {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 9px; font-weight: 600; letter-spacing: 0.2em; text-transform: uppercase;
  color: rgba(255,255,255,0.35); margin-bottom: 4px;
}
.it6__inv-number {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 22px; font-weight: 700; color: #FFD166;
  letter-spacing: 0.04em;
}

/* ── ruled divider ── */
.it6__rule { height: 1px; background: rgba(10,85,86,0.10); }
.it6__rule--dashed {
  height: 1px;
  border: none;
  border-top: 1px dashed rgba(10,85,86,0.14);
  background: none;
}

/* ══════════════════════════════════════
   META ROW (date · status · due)
══════════════════════════════════════ */
.it6__meta-row {
  display: flex; align-items: center;
  padding: 14px 36px;
  gap: 0;
  background: #f7fafa;
  border-bottom: 1px solid rgba(10,85,86,0.08);
}
.it6__meta-cell {
  flex: 1; display: flex; flex-direction: column; gap: 3px;
  padding: 0 20px;
  border-right: 1px dashed rgba(10,85,86,0.12);
}
.it6__meta-cell:first-child { padding-left: 0; }
.it6__meta-cell:last-child  { padding-right: 0; border-right: none; }
.it6__meta-lbl {
  font-size: 9px; font-family: 'IBM Plex Mono', monospace; font-weight: 600;
  letter-spacing: 0.18em; text-transform: uppercase; color: #9ababa;
}
.it6__meta-val {
  font-family: 'IBM Plex Mono', monospace; font-size: 13px; font-weight: 700; color: #0f2a2a;
}

/* status pill */
.it6__status {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 10px; border-radius: 4px;
  font-size: 11px; font-family: 'IBM Plex Mono', monospace; font-weight: 600;
}
.it6__status--paid    { background: #d1fae5; color: #065f46; }
.it6__status--unpaid  { background: #fee2e2; color: #991b1b; }
.it6__status--partial { background: #fef3c7; color: #92400e; }
.it6__status--pending { background: #f0f9ff; color: #075985; }
.it6__status-dot { width: 6px; height: 6px; border-radius: 50%; }

/* ══════════════════════════════════════
   BILLING SECTION
══════════════════════════════════════ */
.it6__billing {
  display: grid; grid-template-columns: 1fr 1fr;
  padding: 22px 36px;
  gap: 28px;
  border-bottom: 1px solid rgba(10,85,86,0.08);
}
.it6__bill-lbl {
  font-family: 'IBM Plex Mono', monospace; font-size: 9px; font-weight: 600;
  letter-spacing: 0.2em; text-transform: uppercase; color: #0a5556;
  border-left: 3px solid #FFD166; padding-left: 8px; margin-bottom: 12px;
}
.it6__client-name {
  font-family: 'IBM Plex Mono', monospace; font-size: 15px; font-weight: 700;
  color: #0f2a2a; margin-bottom: 8px;
}
.it6__client-row {
  display: flex; align-items: flex-start; gap: 6px;
  font-size: 12px; color: #5a8080; margin-bottom: 5px; line-height: 1.4;
}
.it6__client-row svg { color: #0a5556; flex-shrink: 0; margin-top: 1px; }
.it6__gstin-val {
  font-family: 'IBM Plex Mono', monospace; font-size: 10.5px; font-weight: 600;
  color: #0a5556; margin-top: 4px;
}

/* seller repeat (right column) */
.it6__det-pair { display: flex; flex-direction: column; gap: 10px; }
.it6__det-item { display: flex; flex-direction: column; gap: 2px; padding-bottom: 10px; border-bottom: 1px dashed rgba(10,85,86,0.10); }
.it6__det-item:last-child { border-bottom: none; padding-bottom: 0; }
.it6__det-lbl {
  font-family: 'IBM Plex Mono', monospace; font-size: 9px; font-weight: 600;
  letter-spacing: 0.16em; text-transform: uppercase; color: #9ababa;
}
.it6__det-val {
  font-family: 'IBM Plex Mono', monospace; font-size: 13px; font-weight: 700; color: #0f2a2a;
}

/* ══════════════════════════════════════
   TABLE
══════════════════════════════════════ */
.it6__table-wrap { padding: 22px 36px; flex: 1; }
.it6__table-lbl {
  font-family: 'IBM Plex Mono', monospace; font-size: 9px; font-weight: 600;
  letter-spacing: 0.2em; text-transform: uppercase; color: #9ababa; margin-bottom: 12px;
}

.it6__table { width: 100%; border-collapse: collapse; }

.it6__table thead tr {
  border-bottom: 2px solid #0a5556;
}
.it6__table thead th {
  font-family: 'IBM Plex Mono', monospace; font-size: 9.5px; font-weight: 700;
  letter-spacing: 0.14em; text-transform: uppercase; color: #0a5556;
  padding: 10px 10px; text-align: left; background: transparent;
}
.it6__table thead th:first-child { padding-left: 0; }
.it6__table thead th.th-r { text-align: right; padding-right: 0; }
.it6__table thead th.th-c { text-align: center; }

.it6__table tbody tr { border-bottom: 1px solid rgba(10,85,86,0.07); }
.it6__table tbody tr:last-child { border-bottom: 2px solid #0a5556; }
.it6__table tbody tr:hover { background: rgba(10,85,86,0.025); }
.it6__table tbody td { padding: 12px 10px; font-size: 13px; color: #0f2a2a; vertical-align: middle; }
.it6__table tbody td:first-child { padding-left: 0; }
.it6__table tbody td.td-r { text-align: right; padding-right: 0; font-family: 'IBM Plex Mono', monospace; font-weight: 600; color: #5a8080; }
.it6__table tbody td.td-c { text-align: center; font-family: 'IBM Plex Mono', monospace; font-weight: 700; }
.it6__table tbody td.td-total { text-align: right; padding-right: 0; }

.it6__item-name { font-size: 13px; font-weight: 600; color: #0f2a2a; margin-bottom: 4px; }
.it6__item-sub {
  font-family: 'IBM Plex Mono', monospace; font-size: 10px; font-weight: 500;
  color: #9ababa; letter-spacing: 0.04em;
}
.it6__total-mono {
  font-family: 'IBM Plex Mono', monospace; font-size: 14px; font-weight: 700; color: #0a3d3e;
}

/* ══════════════════════════════════════
   FOOTER
══════════════════════════════════════ */
.it6__footer {
  display: flex; align-items: flex-end; gap: 0;
  border-top: 2px solid #0a3d3e;
  margin-top: auto;
}
.it6__notes-area {
  flex: 1; padding: 18px 36px;
  border-right: 1px dashed rgba(10,85,86,0.14);
}
.it6__notes-lbl {
  font-family: 'IBM Plex Mono', monospace; font-size: 9px; font-weight: 600;
  letter-spacing: 0.2em; text-transform: uppercase; color: #9ababa; margin-bottom: 7px;
}
.it6__notes-txt { font-size: 12px; color: #5a8080; line-height: 1.6; }

/* summary */
.it6__summary { width: 230px; flex-shrink: 0; }
.it6__sum-row {
  display: flex; justify-content: space-between;
  padding: 9px 24px;
  border-bottom: 1px dashed rgba(10,85,86,0.10);
}
.it6__sum-lbl { font-size: 11.5px; color: #5a8080; }
.it6__sum-val { font-family: 'IBM Plex Mono', monospace; font-size: 12px; font-weight: 700; color: #0f2a2a; }
.it6__grand-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 14px 24px;
  background: #0a3d3e;
}
.it6__grand-lbl {
  font-family: 'IBM Plex Mono', monospace; font-size: 9px; font-weight: 700;
  letter-spacing: 0.18em; text-transform: uppercase; color: rgba(255,255,255,0.45);
}
.it6__grand-val {
  font-family: 'IBM Plex Mono', monospace; font-size: 18px; font-weight: 700; color: #FFD166;
}

/* bottom bar */
.it6__bottom-bar {
  height: 5px;
  background: linear-gradient(90deg, #FFD166 0%, #f59e0b 20%, #0a5556 50%, #14b8a6 80%, #FFD166 100%);
}

/* print */
@page { size: A4 portrait; margin: 0; }
@media print {
  .it6__wrap { background: none !important; padding: 0 !important; display: block !important; }
  .it6__paper { width: 210mm !important; min-height: 297mm !important; box-shadow: none !important; margin: 0 !important; }
}
@media (max-width: 640px) {
  .it6__billing { grid-template-columns: 1fr; }
  .it6__band-inner { flex-direction: column; gap: 14px; }
  .it6__inv-block { text-align: left; }
  .it6__footer { flex-direction: column; }
  .it6__summary { width: 100%; }
  .it6__meta-row { flex-wrap: wrap; }
}
`;

/* helpers */
const num   = (v) => { const n = Number(v); return isNaN(n) ? 0 : n; };
const fmtIN = (v) => num(v).toLocaleString("en-IN", { minimumFractionDigits: 2 });

const getStatusInfo = (status = "") => {
  const s = status.toLowerCase();
  if (s === "paid")    return { cls: "it6__status--paid",    dot: "#10b981" };
  if (s === "unpaid")  return { cls: "it6__status--unpaid",  dot: "#ef4444" };
  if (s === "partial") return { cls: "it6__status--partial", dot: "#f59e0b" };
  return                      { cls: "it6__status--pending", dot: "#38bdf8" };
};

/* ═══════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════ */
const InvoiceSix = ({ invoiceData }) => {
  const [sellerInfo, setSellerInfo] = useState(null);
  const token = localStorage.getItem("auth_token");

  console.log("invoiceData", invoiceData);

  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        const response = await axios.get(`${baseurl}getBusinessInfo`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("response", response);
        if (response.data.success) setSellerInfo(response.data.data);
      } catch (error) {
        console.error("Error fetching business info for seller:", error);
      }
    };
    fetchBusinessInfo();
  }, [token]);

  const defaultData = {
    invoice_number: "INV-000",
    invoice_date: new Date().toISOString().split("T")[0],
    due_date: "N/A",
    payment_status: "Pending",
    notes: "Thank you for your business.",
    seller: { name: "N/A", gstin: "N/A", phone: "N/A", state: "N/A", email: "", zipcode: "" },
    customer: { name: "Client Name", email: "client@example.com", gstin: "N/A", phone: "N/A", state: "N/A", address: "Client Address Here" },
    items: [],
    gst_breakdown: { CGST: 0, SGST: 0 },
    subtotal: 0, taxable_amount: 0, grand_total: 0,
  };

  const resolvedSeller =
    invoiceData?.invoice_data?.seller ||
    (sellerInfo ? {
      name: sellerInfo.business_name, gstin: sellerInfo.gst || "N/A",
      phone: sellerInfo.whatsapp_number, state: sellerInfo.state,
      city: sellerInfo.city, zipcode: sellerInfo.zipcode,
      email: sellerInfo.contact_email || sellerInfo.email,
    } : defaultData.seller);

  const resolved = invoiceData?.invoice_data
    ? {
        invoice_number: invoiceData.invoice_number || defaultData.invoice_number,
        invoice_date:   invoiceData.invoice_data.invoiceDate || defaultData.invoice_date,
        due_date:       invoiceData.invoice_data.dueDate,
        payment_status: invoiceData.invoice_data.paymentStatus,
        seller:         resolvedSeller,
        customer:       invoiceData.invoice_data.customer || defaultData.customer,
        items:          invoiceData.invoice_data.items || [],
        gst_breakdown:  invoiceData.invoice_data.gstBreakdown || { CGST: 0, SGST: 0 },
        subtotal:       num(invoiceData.invoice_data.itemSubtotal),
        taxable_amount: num(invoiceData.invoice_data.taxableAmount),
        grand_total:    num(invoiceData.grand_total ?? invoiceData.invoice_data.grandTotal),
        notes:          invoiceData.invoice_data.notes || defaultData.notes,
      }
    : { ...defaultData, seller: resolvedSeller };

  const { invoice_number, invoice_date, payment_status, seller, customer, items, gst_breakdown, subtotal, grand_total, notes } = resolved;
  const si = getStatusInfo(payment_status);

  return (
    <>
      <style>{CSS}</style>
      <div className="it6__wrap">
        <div className="it6__paper">

          {/* watermark */}
          <div className="it6__stamp">
            <div className="it6__stamp-inner">
              <div className="it6__stamp-word">TAX</div>
              <div className="it6__stamp-line" />
              <div className="it6__stamp-word">INVOICE</div>
            </div>
          </div>

          <div className="it6__content">

            {/* TOP BAND */}
            <div className="it6__top-band">
              <div className="it6__tab" />
              <div className="it6__band-inner">
                <div>
                  <div className="it6__company-name">{seller.name}</div>
                  {(seller.city || seller.state) && (
                    <div className="it6__seller-row"><FiMapPin size={11} />{seller.city && `${seller.city}, `}{seller.state} {seller.zipcode}</div>
                  )}
                  {seller.phone && <div className="it6__seller-row"><FiPhone size={11} />{seller.phone}</div>}
                  {seller.email && <div className="it6__seller-row"><FiMail  size={11} />{seller.email}</div>}
                  {seller.gstin !== "N/A" && <div className="it6__gstin-mono">GSTIN · {seller.gstin}</div>}
                </div>
                <div className="it6__inv-block">
                  <div className="it6__inv-label">Invoice Number</div>
                  <div className="it6__inv-number">{invoice_number}</div>
                </div>
              </div>
            </div>

            {/* META ROW */}
            <div className="it6__meta-row">
              <div className="it6__meta-cell">
                <div className="it6__meta-lbl">Issue Date</div>
                <div className="it6__meta-val">{invoice_date}</div>
              </div>
              <div className="it6__meta-cell">
                <div className="it6__meta-lbl">Due Date</div>
                <div className="it6__meta-val">{resolved.due_date || "N/A"}</div>
              </div>
              <div className="it6__meta-cell">
                <div className="it6__meta-lbl">Status</div>
                <div className="it6__meta-val">
                  <span className={`it6__status ${si.cls}`}>
                    <span className="it6__status-dot" style={{ background: si.dot }} />
                    {payment_status}
                  </span>
                </div>
              </div>
              <div className="it6__meta-cell">
                <div className="it6__meta-lbl">Total Due</div>
                <div className="it6__meta-val" style={{ color: "#0a5556" }}>₹{fmtIN(grand_total)}</div>
              </div>
            </div>

            {/* BILLING */}
            <div className="it6__billing">
              <div>
                <div className="it6__bill-lbl">Bill To</div>
                <div className="it6__client-name">{customer.name}</div>
                {customer.address && <div className="it6__client-row"><FiMapPin size={11} />{customer.address}</div>}
                {customer.phone   && <div className="it6__client-row"><FiPhone  size={11} />{customer.phone}</div>}
                {customer.email   && <div className="it6__client-row"><FiMail   size={11} />{customer.email}</div>}
                {customer.gstin !== "N/A" && customer.gstin && (
                  <div className="it6__gstin-val">GSTIN: {customer.gstin}</div>
                )}
              </div>
              <div>
                <div className="it6__bill-lbl">Invoice Details</div>
                <div className="it6__det-pair">
                  <div className="it6__det-item">
                    <div className="it6__det-lbl">Invoice No</div>
                    <div className="it6__det-val">{invoice_number}</div>
                  </div>
                  <div className="it6__det-item">
                    <div className="it6__det-lbl">Issue Date</div>
                    <div className="it6__det-val">{invoice_date}</div>
                  </div>
                  <div className="it6__det-item">
                    <div className="it6__det-lbl">Due Date</div>
                    <div className="it6__det-val">{resolved.due_date || "N/A"}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* TABLE */}
            <div className="it6__table-wrap">
              <div className="it6__table-lbl">Line Items</div>
              <table className="it6__table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th className="th-c">Qty</th>
                    <th className="th-r">Rate</th>
                    <th className="th-r">GST %</th>
                    <th className="th-r">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length > 0 ? items.map((item, i) => {
                    const lineTotal = num(item.qty) * num(item.rate) * (1 + num(item.tax) / 100);
                    return (
                      <tr key={i}>
                        <td>
                          <div className="it6__item-name">{item.description}</div>
                          <div className="it6__item-sub">HSN · {item.hsn || "—"}</div>
                        </td>
                        <td className="td-c">{item.qty}</td>
                        <td className="td-r">₹{fmtIN(item.rate)}</td>
                        <td className="td-r">{item.tax}%</td>
                        <td className="td-total"><span className="it6__total-mono">₹{fmtIN(lineTotal)}</span></td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan="5" style={{ textAlign: "center", padding: "30px", color: "#9ababa", fontFamily: "'IBM Plex Mono',monospace" }}>No items found</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* FOOTER */}
            <div className="it6__footer">
              <div className="it6__notes-area">
                <div className="it6__notes-lbl">Notes</div>
                <div className="it6__notes-txt">{notes || "Thank you for your business."}</div>
              </div>
              <div className="it6__summary">
                <div className="it6__sum-row">
                  <span className="it6__sum-lbl">Subtotal</span>
                  <span className="it6__sum-val">₹{fmtIN(subtotal)}</span>
                </div>
                <div className="it6__sum-row">
                  <span className="it6__sum-lbl">CGST</span>
                  <span className="it6__sum-val">₹{fmtIN(gst_breakdown.CGST)}</span>
                </div>
                <div className="it6__sum-row">
                  <span className="it6__sum-lbl">SGST</span>
                  <span className="it6__sum-val">₹{fmtIN(gst_breakdown.SGST)}</span>
                </div>
                <div className="it6__grand-row">
                  <span className="it6__grand-lbl">Total Due</span>
                  <span className="it6__grand-val">₹{fmtIN(grand_total)}</span>
                </div>
              </div>
            </div>

            <div className="it6__bottom-bar" />

          </div>
        </div>
      </div>
    </>
  );
};

export default InvoiceSix;
